import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import { Chip, Stack, styled } from "@mui/material"
import type {
  DateRange,
  DateRangeValidationError,
  DateTimeRangeValidationError,
  PickerChangeHandlerContext,
} from "@mui/x-date-pickers-pro"
import {
  DateRangePicker,
  DateTimeRangePicker,
  LocalizationProvider,
  MultiInputDateTimeRangeField,
  pickersOutlinedInputClasses,
} from "@mui/x-date-pickers-pro"
import { pickerDayClasses } from "@mui/x-date-pickers/PickerDay"
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs"
import type { Dayjs } from "dayjs"
import * as React from "react"
import { extraColors } from "../../utils/theme"
import {
  getDates,
  isRelative,
  isSameLookback,
  type DynamicDateRange,
  type RelativeLookback,
} from "../../utils/dates"

export enum RelativeDateRangeChoiceId {
  TwentyFourHours = "24h",
  ThreeDays = "3d",
  SevenDays = "7d",
  TwoWeeks = "2w",
  ThreeWeeks = "3w",
  OneMonth = "1mo",
  ThirtyDays = "30d",
  ThreeMonths = "3mo",
  TwelveMonths = "12mo",
}

export interface RelativeDateRangeChoices extends RelativeLookback {
  id: RelativeDateRangeChoiceId
  /** Display label shown to the user. */
  label: string
}

export const RELATIVE_DATERANGE_CHOICES: RelativeDateRangeChoices[] = [
  {
    id: RelativeDateRangeChoiceId.TwentyFourHours,
    label: "24 hours",
    lookbackValue: 24,
    lookbackUnit: "hours",
  },
  {
    id: RelativeDateRangeChoiceId.ThreeDays,
    label: "3 days",
    lookbackValue: 3,
    lookbackUnit: "days",
  },
  {
    id: RelativeDateRangeChoiceId.SevenDays,
    label: "7 days",
    lookbackValue: 7,
    lookbackUnit: "days",
  },
  {
    id: RelativeDateRangeChoiceId.TwoWeeks,
    label: "2 weeks",
    lookbackValue: 14,
    lookbackUnit: "days",
  },
  {
    id: RelativeDateRangeChoiceId.ThreeWeeks,
    label: "3 weeks",
    lookbackValue: 21,
    lookbackUnit: "days",
  },
  {
    id: RelativeDateRangeChoiceId.OneMonth,
    label: "1 month",
    lookbackValue: 1,
    lookbackUnit: "months",
  },
  {
    id: RelativeDateRangeChoiceId.ThirtyDays,
    label: "30 days",
    lookbackValue: 30,
    lookbackUnit: "days",
  },
  {
    id: RelativeDateRangeChoiceId.ThreeMonths,
    label: "3 months",
    lookbackValue: 3,
    lookbackUnit: "months",
  },
  {
    id: RelativeDateRangeChoiceId.TwelveMonths,
    label: "12 months",
    lookbackValue: 12,
    lookbackUnit: "months",
  },
]

interface DateRangePickerProps {
  initialValue?: DynamicDateRange
  maxDate?: Dayjs
  minDate?: Dayjs
  onChange: (value: DynamicDateRange) => void
  showTime?: boolean
  showRelativeDateRanges?: boolean
  visibleRelativeDateRangeChoices?: RelativeDateRangeChoiceId[]
}

const StyledMultiInputDateTimeRangeField = styled(MultiInputDateTimeRangeField)(
  {
    [`& .${pickersOutlinedInputClasses.root}:hover .${pickersOutlinedInputClasses.notchedOutline}`]:
      {
        borderColor: extraColors.translucentWhite,
      },
  }
) as typeof MultiInputDateTimeRangeField

const RelativeDateRangeChip = styled(Chip)({
  height: 24,
  borderRadius: 12,
  fontSize: 12,
  "&.MuiChip-filledPrimary": {
    backgroundColor: extraColors.status.navyLight,
  },
  color: extraColors.white,
})

export const PHCDateRangePicker = ({
  initialValue,
  maxDate,
  minDate,
  onChange,
  showTime = true,
  showRelativeDateRanges = false,
  visibleRelativeDateRangeChoices = [
    RelativeDateRangeChoiceId.TwentyFourHours,
    RelativeDateRangeChoiceId.ThreeDays,
    RelativeDateRangeChoiceId.SevenDays,
  ],
}: DateRangePickerProps) => {
  const [value, setValue] = React.useState<DynamicDateRange>(
    initialValue ?? [null, null]
  )

  React.useEffect(() => {
    if (initialValue) {
      setValue(initialValue)
    }
  }, [initialValue])

  const handleChange = (
    newVal: DateRange<Dayjs>,
    context: PickerChangeHandlerContext<
      DateTimeRangeValidationError | DateRangeValidationError
    >
  ) => {
    setValue(newVal)
    // there might be validation errors while the user is typing, we don't want to update the state in this case
    if (!context.validationError[0] && !context.validationError[1]) {
      onChange(newVal)
    }
  }

  const handleRelativeDateRangeClick = (
    relativeDateRange: RelativeDateRangeChoices
  ) => {
    setValue(relativeDateRange)
    onChange(relativeDateRange)
  }

  const calendarIcon = (
    <CalendarTodayIcon
      sx={{ fontSize: 16, color: extraColors.status.navyLight, opacity: 0.8 }}
    />
  )

  const Picker = showTime ? DateTimeRangePicker : DateRangePicker

  const valueIsRelative = isRelative(value)
  const valueAsDates = getDates(value)

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {showRelativeDateRanges && (
        <Stack direction="row" spacing={1} sx={{ mb: 2.5, rowGap: 1 }}>
          {RELATIVE_DATERANGE_CHOICES.filter(relativeDateRange =>
            visibleRelativeDateRangeChoices.includes(relativeDateRange.id)
          ).map(relativeDateRange => {
            const selected = valueIsRelative
              ? isSameLookback(relativeDateRange, value)
              : false
            return (
              <RelativeDateRangeChip
                key={relativeDateRange.id}
                label={relativeDateRange.label}
                size="small"
                color={selected ? "primary" : "default"}
                variant={selected ? "filled" : "outlined"}
                onClick={() => handleRelativeDateRangeClick(relativeDateRange)}
              />
            )
          })}
        </Stack>
      )}
      <Picker
        value={valueAsDates}
        onChange={handleChange}
        format={showTime ? "M/DD/YYYY hh:mm A" : "M/D/YYYY"}
        maxDate={maxDate}
        minDate={minDate}
        slots={{
          field: StyledMultiInputDateTimeRangeField,
        }}
        slotProps={{
          textField: ({ position }: any) => ({
            size: "small",
            variant: "outlined",
            label: position === "start" ? "Start" : "End",
            InputProps: {
              endAdornment: calendarIcon,
            },
          }),
          day: {
            sx: {
              color: extraColors.white,
              [`&.${pickerDayClasses.disabled}`]: {
                color: extraColors.disabled,
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  )
}
